import { useCallback } from "react";
import { DayModifiers } from "react-day-picker";
import { startOfDay, endOfDay, isSameDay } from "date-fns";

type DateRange = {
    from: Date | null | undefined;
    to: Date | null | undefined;
};

interface UseDateRangeClickOptions<T extends DateRange = DateRange> {
    date: T;
    setDate: (date: T | ((prev: T) => T)) => void;
    setCurrentPage?: (page: number) => void;
    emptyValue?: T["to"];
}

export const useDateRangeClick = <T extends DateRange = DateRange>({
    date,
    setDate,
    setCurrentPage,
    emptyValue,
}: UseDateRangeClickOptions<T>) => {
    const handleDayClick = useCallback(
        (day: Date, _modifiers: DayModifiers, _e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (date.from && date.to) {
                setDate({ ...date, from: day, to: emptyValue } as T);
                setCurrentPage?.(1);
                return;
            }

            if (!date.from) {
                setDate({ ...date, from: day, to: emptyValue } as T);
                setCurrentPage?.(1);
                return;
            }

            const fromDate = date.from as Date;
            const normalizedFrom = startOfDay(fromDate);
            const normalizedDay = startOfDay(day);

            const newRange = isSameDay(day, fromDate)
                ? { from: startOfDay(day), to: endOfDay(day) }
                : normalizedDay >= normalizedFrom
                ? { from: normalizedFrom, to: endOfDay(day) }
                : { from: normalizedDay, to: endOfDay(fromDate) };

            setDate({ ...date, ...newRange } as T);
            setCurrentPage?.(1);
        },
        [date, setDate, setCurrentPage, emptyValue]
    );

    return handleDayClick;
};
